import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Card } from 'components/ui/card';
import { Button } from 'components/ui/button';

type OAuthStatus = 'processing' | 'success' | 'error';

const POPUP_AUTO_CLOSE_MS = 2000;

/**
 * OAuth callback page for completing integration connections
 * 
 * This page handles the OAuth redirect after the user authorizes
 * the integration provider. It forwards the callback params to the
 * main integrations page, which completes the backend callback once.
 * 
 * When opened in a popup window, it automatically passes the result
 * back to the opener window and closes itself.
 * 
 * URL parameters:
 * - flow_id: Unique identifier for the OAuth flow
 * - status: OAuth status (success/error)
 * - source_connection_id: Airweave source connection ID
 * - error: Error message if status is error
 */
export default function OAuthCompletePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<OAuthStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isPopup, setIsPopup] = useState(false);

  useEffect(() => {
    const checkIfPopup = window.opener && window.opener !== window;

    setIsPopup(checkIfPopup);

    if (checkIfPopup) {
      const params = Object.fromEntries(searchParams.entries());

      const handleParentMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data?.type === 'OAUTH_CALLBACK_ACK') {
          window.close();
        }
      };

      window.addEventListener('message', handleParentMessage);

      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage(
            {
              type: 'OAUTH_CALLBACK',
              params,
            },
            window.location.origin
          );
        } catch {
          window.removeEventListener('message', handleParentMessage);
        }
      }

      return () => {
        window.removeEventListener('message', handleParentMessage);
      };
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isPopup) {
      return;
    }

    const timer = window.setTimeout(() => {
      window.close();
    }, POPUP_AUTO_CLOSE_MS);

    return () => window.clearTimeout(timer);
  }, [isPopup]);

  useEffect(() => {
    if (isPopup) {
      return;
    }

    const flowId = searchParams.get('flow_id');
    const oauthStatus = searchParams.get('status');
    const sourceConnectionId = searchParams.get('source_connection_id');
    const error = searchParams.get('error');

    if (!flowId && !oauthStatus && !sourceConnectionId && !error) {
      setStatus('error');
      setErrorMessage('Missing OAuth callback parameters');
      return;
    }

    setStatus('success');

    const timeoutId = window.setTimeout(() => {
      const queryString = searchParams.toString();
      navigate(
        {
          pathname: '/knowledge-base',
          search: queryString ? `?tab=integrations&${queryString}` : '?tab=integrations',
        },
        { replace: true }
      );
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchParams, navigate, isPopup]);

  if (isPopup) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md rounded-lg border border-border bg-card p-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <CheckCircle className="h-12 w-12 text-success-green" />
            <h2 className="text-xl font-semibold text-foreground">Authorization successful</h2>
            <p className="text-sm text-muted-foreground">
              Connection is being completed in the main window.
            </p>
            <p className="text-xs text-muted-foreground">
              This window will close automatically in 2 seconds.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md rounded-lg border border-border bg-card p-8">
        {status === 'processing' && (
          <div className="flex flex-col items-center gap-4 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Processing authorization</h2>
            <p className="text-sm text-muted-foreground">
              Please wait while we return you to integrations...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-4 text-center">
            <CheckCircle className="h-12 w-12 text-success-green" />
            <h2 className="text-xl font-semibold text-foreground">Authorization successful</h2>
            <p className="text-sm text-muted-foreground">
              Returning to integrations to complete the connection...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center gap-4 text-center">
            <XCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold text-foreground">Connection failed</h2>
            <p className="text-sm text-muted-foreground">
              {errorMessage || 'An error occurred while completing the connection.'}
            </p>
            <Button
              className="mt-4"
              onClick={() => navigate('/knowledge-base?tab=integrations', { replace: true })}
              type="button"
            >
              Return to Integrations
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
